import {Parameter, QrSwissPaymentContentFormat} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import FunctionHelper from "../helper/FunctionHelper";
import {AddressType, SwissQrCodeFormAddress} from "./SwissQrCodeFormAddress";

export enum CurrencyType {
	EUR = "EUR",
	CHF = "CHF"
}

export class SwissQrCodeForm implements Parameter {
	public version: string;
	public codingType: string;
	public account: string;
	public creditor: SwissQrCodeFormAddress;
	public debtor: SwissQrCodeFormAddress;
	public amount: number;
	public currency: CurrencyType;
	public referenceType: string;
	public reference: string;
	public unstructuredMessage: string;

	constructor(data: any) {
		this.version = FunctionHelper.exists(data?.version) ? data.version : "0200";
		this.codingType = FunctionHelper.exists(data?.codingType) ? data.codingType : "1";
		this.account = FunctionHelper.exists(data?.account) ? data.account : "";
		this.creditor = SwissQrCodeFormAddress.fromJson(data?.creditor);
		this.debtor = SwissQrCodeFormAddress.fromJson(data?.debtor);
		this.amount = FunctionHelper.exists(data?.amount) ? data.amount : 0;
		this.currency = FunctionHelper.exists(data?.currency) ? data.currency : CurrencyType.EUR;
		this.referenceType = FunctionHelper.exists(data?.referenceType) ? data.referenceType : "NON";
		this.reference = FunctionHelper.exists(data?.reference) ? data.reference : "";
		this.unstructuredMessage = FunctionHelper.exists(data?.unstructuredMessage) ? data.unstructuredMessage : "";
	}

	public static getVersionDefault(): string {
		return "0200";
	}

	public static getCodingTypeDefault(): string {
		return "1";
	}

	public static getAccountDefault(): string {
		return "";
	}

	public static getAmountDefault(): number {
		return 0;
	}

	public static getAmountMin(): number {
		return 0;
	}

	public static getCurrencyDefault(): CurrencyType {
		return CurrencyType.EUR;
	}

	public static getReferenceTypeDefault(): string {
		return "NON";
	}

	public updateReferenceType(): void {
		let ref: string = this.reference.trim();
		if (!FunctionHelper.isEmpty(ref)) {
			if (ref.startsWith("RF")) {
				this.referenceType = "SCOR";
			} else if (ref.length > 0) {
				this.referenceType = "QRR";
			} else {
				this.referenceType = "NON";
			}
		} else {
			this.referenceType = "NON";
		}
	}

	public static getReferenceDefault(): string {
		return "";
	}

	public static getUnstructuredMessageDefault(): string {
		return "";
	}

	public static fromJson(data: any): SwissQrCodeForm {
		if (!FunctionHelper.exists(data)) {
			return data;
		}

		return new SwissQrCodeForm(data);
	}

	public toJson(): any {
		return {
			'version': this.version,
			'codingType': this.codingType,
			'account': this.account,
			'creditor': this.creditor?.toJson(),
			'debtor': this.debtor?.toJson(),
			'amount': this.amount,
			'currency': this.currency,
			'reference': this.reference,
			'unstructuredMessage': this.unstructuredMessage
		};
	}

	public clone(): SwissQrCodeForm {
		return SwissQrCodeForm.fromJson(this.toJson());
	}

	public static fromSwissQrCode(data: string, format: QrSwissPaymentContentFormat): SwissQrCodeForm {
		switch (format) {
			case QrSwissPaymentContentFormat.Json:
				let jsonData: any = JSON.parse(data);

				let version: string = jsonData?.version;
				switch (jsonData?.version) {
					case "V2_0":
						version = "0200";
						break;
				}

				let debtorType: string = jsonData?.debtor?.type;
				switch (jsonData?.debtor?.type) {
					case "STRUCTURED":
						debtorType = AddressType.STRUCTURED;
						break;
					case "COMBINED_ELEMENTS":
						debtorType = AddressType.COMBINED_ELEMENTS;
						break;
				}

				let creditorType: string = jsonData?.creditor?.type;
				switch (jsonData?.creditor?.type) {
					case "STRUCTURED":
						creditorType = AddressType.STRUCTURED;
						break;
					case "COMBINED_ELEMENTS":
						creditorType = AddressType.COMBINED_ELEMENTS;
						break;
				}

				return SwissQrCodeForm.fromJson({
					version: version,
					codingType: jsonData?.codingType,
					account: jsonData?.account,
					creditor: {
						type: creditorType,
						name: jsonData?.creditor?.name,
						addressLine1: jsonData?.creditor?.addressLine1,
						addressLine2: jsonData?.creditor?.addressLine2,
						street: jsonData?.creditor?.street,
						houseNumber: jsonData?.creditor?.houseNo,
						postalCode: jsonData?.creditor?.postalCode,
						location: jsonData?.creditor?.town,
						countryCode: jsonData?.creditor?.countryCode,
					},
					debtor: {
						type: debtorType,
						name: jsonData?.debtor?.name,
						addressLine1: jsonData?.debtor?.addressLine1,
						addressLine2: jsonData?.debtor?.addressLine2,
						street: jsonData?.debtor?.street,
						houseNumber: jsonData?.debtor?.houseNo,
						postalCode: jsonData?.debtor?.postalCode,
						location: jsonData?.debtor?.town,
						countryCode: jsonData?.debtor?.countryCode,
					},
					amount: jsonData?.amount,
					currency: jsonData?.currency,
					referenceType: jsonData?.referenceType,
					reference: jsonData?.reference,
					unstructuredMessage: jsonData?.unstructuredMessage
				} as SwissQrCodeForm);
			case QrSwissPaymentContentFormat.Plain:
				var textData: Array<string> = data.split(/\r?\n|\r|\n/g);

				return SwissQrCodeForm.fromJson({
					version: FunctionHelper.applyValue(textData[1], SwissQrCodeForm.getVersionDefault()),
					codingType: FunctionHelper.applyValue(textData[2], SwissQrCodeForm.getCodingTypeDefault()),
					account: FunctionHelper.applyValue(textData[3], SwissQrCodeForm.getAccountDefault()),
					creditor: {
						type: FunctionHelper.applyValue(textData[4], SwissQrCodeFormAddress.getTypeDefault()),
						name: FunctionHelper.applyValue(textData[5], SwissQrCodeFormAddress.getNameDefault()),
						addressLine1: FunctionHelper.applyValue(
							textData[6], SwissQrCodeFormAddress.getAddressLine1Default()
						),
						addressLine2: FunctionHelper.applyValue(
							textData[7], SwissQrCodeFormAddress.getAddressLine2Default()
						),
						street: FunctionHelper.applyValue(textData[6], SwissQrCodeFormAddress.getStreetDefault()),
						houseNumber: FunctionHelper.applyValue(
							textData[7], SwissQrCodeFormAddress.getHouseNumberDefault()
						),
						postalCode: FunctionHelper.applyValue(
							textData[8], SwissQrCodeFormAddress.getPostalCodeDefault()
						),
						location: FunctionHelper.applyValue(textData[9], SwissQrCodeFormAddress.getLocationDefault()),
						countryCode: FunctionHelper.applyValue(
							textData[10], SwissQrCodeFormAddress.getCountryCodeDefault()
						)
					},
					debtor: {
						type: FunctionHelper.applyValue(textData[20], SwissQrCodeFormAddress.getTypeDefault()),
						name: FunctionHelper.applyValue(textData[21], SwissQrCodeFormAddress.getNameDefault()),
						addressLine1: FunctionHelper.applyValue(
							textData[22], SwissQrCodeFormAddress.getAddressLine1Default()
						),
						addressLine2: FunctionHelper.applyValue(
							textData[23], SwissQrCodeFormAddress.getAddressLine2Default()
						),
						street: FunctionHelper.applyValue(textData[22], SwissQrCodeFormAddress.getStreetDefault()),
						houseNumber: FunctionHelper.applyValue(
							textData[23], SwissQrCodeFormAddress.getHouseNumberDefault()
						),
						postalCode: FunctionHelper.applyValue(
							textData[24], SwissQrCodeFormAddress.getPostalCodeDefault()
						),
						location: FunctionHelper.applyValue(textData[25], SwissQrCodeFormAddress.getLocationDefault()),
						countryCode: FunctionHelper.applyValue(
							textData[26], SwissQrCodeFormAddress.getCountryCodeDefault()
						)
					},
					amount: FunctionHelper.applyValue(textData[18], SwissQrCodeForm.getAmountDefault()),
					currency: FunctionHelper.applyValue(textData[19], SwissQrCodeForm.getCurrencyDefault()),
					referenceType: FunctionHelper.applyValue(textData[27], SwissQrCodeForm.getReferenceTypeDefault()),
					reference: FunctionHelper.applyValue(textData[28], SwissQrCodeForm.getReferenceDefault()),
					unstructuredMessage: FunctionHelper.applyValue(
						textData[29], SwissQrCodeForm.getUnstructuredMessageDefault()
					)
				} as SwissQrCodeForm);
		}

		return new SwissQrCodeForm({});
	}

	public toSwissQrCode(format: QrSwissPaymentContentFormat, prepareSubmission?: boolean): string {
		let result: string = "";

		switch (format) {
			case QrSwissPaymentContentFormat.Json:
				result +=
					"{\n";

				if (!prepareSubmission) {
					result +=
						"	\"version\": \"" + this.version + "\",\n" +
						"	\"codingType\": \"" + this.codingType + "\",\n";
				}

				result +=
					"	\"account\": \"" + this.account + "\",\n" +
					"	\"creditor\": {\n";

				if (!prepareSubmission) {
					result +=
						"		\"type\": \"" + this.creditor.type + "\",\n";
				}

				result +=
					"		\"name\": \"" + this.creditor.name + "\",\n";

				if (this.creditor.type === AddressType.STRUCTURED) {
					result +=
						"		\"street\": \"" + this.creditor.street + "\",\n" +
						"		\"houseNo\": \"" + this.creditor.houseNumber + "\",\n";
				} else {
					result +=
						"		\"addressLine1\": \"" + this.creditor.addressLine1 + "\",\n" +
						"		\"addressLine2\": \"" + this.creditor.addressLine2 + "\",\n";
				}

				result +=
					"		\"postalCode\": \"" + this.creditor.postalCode + "\",\n" +
					"		\"town\": \"" + this.creditor.location + "\",\n" +
					"		\"countryCode\": \"" + this.creditor.countryCode + "\"\n" +
					"	},\n" +
					"	\"debtor\": {\n";

				if (!prepareSubmission) {
					result +=
						"		\"type\": \"" + this.debtor.type + "\",\n";
				}

				result +=
					"		\"name\": \"" + this.debtor.name + "\",\n";

				if (this.debtor.type === AddressType.STRUCTURED) {
					result +=
						"		\"street\": \"" + this.debtor.street + "\",\n" +
						"		\"houseNo\": \"" + this.debtor.houseNumber + "\",\n";
				} else {
					result +=
						"		\"addressLine1\": \"" + this.debtor.addressLine1 + "\",\n" +
						"		\"addressLine2\": \"" + this.debtor.addressLine2 + "\",\n";
				}

				result +=
					"		\"postalCode\": \"" + this.debtor.postalCode + "\",\n" +
					"		\"town\": \"" + this.debtor.location + "\",\n" +
					"		\"countryCode\": \"" + this.debtor.countryCode + "\"\n" +
					"	},\n" +
					"	\"amount\": " + this.amount + ",\n" +
					"	\"currency\": \"" + this.currency + "\",\n" +
					"	\"referenceType\": \"" + this.referenceType + "\",\n" +
					"	\"reference\": \"" + this.reference + "\",\n" +
					"	\"unstructuredMessage\": \"" + this.unstructuredMessage + "\"\n" +
					"}";
				break;
			case QrSwissPaymentContentFormat.Plain:
				result +=
					"SPC\n" +
					this.version + "\n" +
					this.codingType + "\n" +
					this.account + "\n" +
					this.creditor.type + "\n" +
					this.creditor.name + "\n";

				if (this.creditor.type === AddressType.STRUCTURED) {
					result +=
						this.creditor.street + "\n" +
						this.creditor.houseNumber + "\n";
				} else {
					result +=
						this.creditor.addressLine1 + "\n" +
						this.creditor.addressLine2 + "\n";
				}

				result +=
					this.creditor.postalCode + "\n" +
					this.creditor.location + "\n" +
					this.creditor.countryCode + "\n" +
					"\n" +
					"\n" +
					"\n" +
					"\n" +
					"\n" +
					"\n" +
					"\n" +
					this.amount + "\n" +
					this.currency + "\n" +
					this.debtor.type + "\n" +
					this.debtor.name + "\n";

				if (this.debtor.type === AddressType.STRUCTURED) {
					result +=
						this.debtor.street + "\n" +
						this.debtor.houseNumber + "\n";
				} else {
					result +=
						this.debtor.addressLine1 + "\n" +
						this.debtor.addressLine2 + "\n";
				}

				result +=
					this.debtor.postalCode + "\n" +
					this.debtor.location + "\n" +
					this.debtor.countryCode + "\n" +
					this.referenceType + "\n" +
					this.reference + "\n" +
					this.unstructuredMessage + "\n" +
					"EPD";
				break;
		}

		return result;
	}
}